// DEFCON TERMINAL - Modem Sound Generator
// Recreates classic 1980s modem sounds procedurally

import { SoundGenerator } from './SoundGenerator';

/**
 * DTMF (Dual-Tone Multi-Frequency) tone frequencies
 * Standard telephone keypad tones
 */
const DTMF_FREQUENCIES: Record<string, [number, number]> = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  '0': [941, 1336],
  '*': [941, 1209],
  '#': [941, 1477],
  '-': [0, 0], // Pause for dashes in phone numbers
};

/**
 * Extended DTMF frequencies including A, B, C, D tones
 * Used in military/trunk lines and international calls in 1980s
 */
const DTMF_EXTENDED: Record<string, [number, number]> = {
  ...DTMF_FREQUENCIES,
  'A': [697, 1633],  // Flash/priority
  'B': [770, 1633],  // Flash override
  'C': [852, 1633],  // Immediate
  'D': [941, 1633],  // Priority override
};

/**
 * International country codes (2-3 digits) for authentic dialing
 */
const COUNTRY_CODES = [
  '1',    // USA/Canada
  '7',    // Russia/USSR
  '33',   // France
  '44',   // UK
  '49',   // West Germany
  '81',   // Japan
  '86',   // China
  '39',   // Italy
  '34',   // Spain
  '31',   // Netherlands
  '46',   // Sweden
  '47',   // Norway
  '358',  // Finland
  '48',   // Poland
  '43',   // Austria
  '41',   // Switzerland
  '32',   // Belgium
  '420',  // Czechoslovakia
  '36',   // Hungary
  '40',   // Romania
  '359',  // Bulgaria
  '90',   // Turkey
  '972',  // Israel
  '20',   // Egypt
  '27',   // South Africa
  '61',   // Australia
  '64',   // New Zealand
  '55',   // Brazil
  '54',   // Argentina
  '52',   // Mexico
];

/**
 * ModemSounds - Generates authentic 1980s modem connection sounds
 */
export class ModemSounds {
  private context: AudioContext;
  private soundGenerator: SoundGenerator;
  private masterGain: GainNode;

  constructor(context: AudioContext, soundGenerator: SoundGenerator) {
    this.context = context;
    this.soundGenerator = soundGenerator;
    this.masterGain = context.createGain();
    this.masterGain.connect(context.destination);
  }

  /**
   * Set master volume for modem sounds
   */
  setVolume(volume: number): void {
    this.masterGain.gain.setValueAtTime(
      Math.max(0, Math.min(1, volume)),
      this.context.currentTime
    );
  }

  /**
   * Generate US dial tone (350Hz + 440Hz combined)
   */
  generateDialTone(duration: number = 1.0): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const freq1 = 350;
    const freq2 = 440;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const sample1 = Math.sin(2 * Math.PI * freq1 * t);
      const sample2 = Math.sin(2 * Math.PI * freq2 * t);

      // Combine and normalize
      data[i] = (sample1 + sample2) * 0.2;
    }

    return buffer;
  }

  /**
   * Generate a single DTMF tone for a digit
   */
  generateDTMFTone(digit: string, duration: number = 0.1): AudioBuffer | null {
    const frequencies = DTMF_FREQUENCIES[digit];
    if (!frequencies) return null;

    // Handle pause for dashes
    if (frequencies[0] === 0 && frequencies[1] === 0) {
      const sampleRate = this.context.sampleRate;
      const bufferLength = Math.ceil(sampleRate * 0.05); // Short pause
      const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
      return buffer;
    }

    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const [lowFreq, highFreq] = frequencies;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const low = Math.sin(2 * Math.PI * lowFreq * t);
      const high = Math.sin(2 * Math.PI * highFreq * t);

      // Quick attack/release envelope
      let envelope = 1;
      const attackSamples = sampleRate * 0.005;
      const releaseSamples = sampleRate * 0.01;

      if (i < attackSamples) {
        envelope = i / attackSamples;
      } else if (i > bufferLength - releaseSamples) {
        envelope = (bufferLength - i) / releaseSamples;
      }

      data[i] = (low + high) * 0.15 * envelope;
    }

    return buffer;
  }

  /**
   * Generate DTMF sequence for a phone number
   */
  generatePhoneDialing(phoneNumber: string): AudioBuffer {
    const toneDuration = 0.1;
    const gapDuration = 0.05;
    const digits = phoneNumber.replace(/[^0-9*#-]/g, '');
    const totalDuration = digits.length * (toneDuration + gapDuration);

    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * totalDuration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    let currentSample = 0;
    const samplesPerTone = Math.ceil(sampleRate * toneDuration);
    const samplesPerGap = Math.ceil(sampleRate * gapDuration);

    for (const digit of digits) {
      const toneBuffer = this.generateDTMFTone(digit, toneDuration);
      if (toneBuffer) {
        const toneData = toneBuffer.getChannelData(0);
        for (let i = 0; i < toneData.length && currentSample + i < bufferLength; i++) {
          data[currentSample + i] = toneData[i];
        }
      }
      currentSample += samplesPerTone + samplesPerGap;
    }

    return buffer;
  }

  /**
   * Generate carrier tone (1200Hz or 2400Hz)
   */
  generateCarrierTone(frequency: number = 1200, duration: number = 0.5): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;

      // FSK-like modulation
      const modulation = Math.sin(2 * Math.PI * 10 * t) * 50;
      const sample = Math.sin(2 * Math.PI * (frequency + modulation) * t);

      // Envelope
      let envelope = 1;
      const rampSamples = sampleRate * 0.02;
      if (i < rampSamples) {
        envelope = i / rampSamples;
      } else if (i > bufferLength - rampSamples) {
        envelope = (bufferLength - i) / rampSamples;
      }

      data[i] = sample * 0.25 * envelope;
    }

    return buffer;
  }

  /**
   * Generate handshake noise - the classic modem screech
   */
  generateHandshakeNoise(duration: number = 1.5): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    // Multiple frequency components that shift during handshake
    const baseFreqs = [1200, 2400, 1800, 2100];
    let phaseAccumulators = [0, 0, 0, 0];

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const progress = i / bufferLength;
      let sample = 0;

      // Phase 1: Initial carrier (0-20%)
      if (progress < 0.2) {
        const localProgress = progress / 0.2;
        const freq = 1200 + localProgress * 600;
        phaseAccumulators[0] += (2 * Math.PI * freq) / sampleRate;
        sample += Math.sin(phaseAccumulators[0]) * 0.3;
      }
      // Phase 2: Frequency negotiation (20-50%)
      else if (progress < 0.5) {
        const localProgress = (progress - 0.2) / 0.3;
        // Rapid frequency hopping
        const hopRate = 20 + localProgress * 50;
        const freqIndex = Math.floor(t * hopRate) % baseFreqs.length;
        const freq = baseFreqs[freqIndex];
        phaseAccumulators[1] += (2 * Math.PI * freq) / sampleRate;
        sample += Math.sin(phaseAccumulators[1]) * 0.25;

        // Add some noise bursts
        if (Math.random() < 0.1) {
          sample += (Math.random() * 2 - 1) * 0.15;
        }
      }
      // Phase 3: Data sync pattern (50-80%)
      else if (progress < 0.8) {
        const localProgress = (progress - 0.5) / 0.3;
        // Alternating tones
        const altFreq = Math.floor(t * 30) % 2 === 0 ? 2100 : 1200;
        phaseAccumulators[2] += (2 * Math.PI * altFreq) / sampleRate;
        sample += Math.sin(phaseAccumulators[2]) * 0.2;

        // FSK data simulation
        const dataFreq = 1800 + Math.sin(t * 100) * 300;
        phaseAccumulators[3] += (2 * Math.PI * dataFreq) / sampleRate;
        sample += Math.sin(phaseAccumulators[3]) * 0.15;
      }
      // Phase 4: Connection established (80-100%)
      else {
        // Settling into stable carrier
        const freq = 1200;
        phaseAccumulators[0] += (2 * Math.PI * freq) / sampleRate;
        sample += Math.sin(phaseAccumulators[0]) * 0.2;

        // Fade out
        const fadeProgress = (progress - 0.8) / 0.2;
        sample *= (1 - fadeProgress);
      }

      // Overall envelope
      let envelope = 1;
      const rampSamples = sampleRate * 0.05;
      if (i < rampSamples) {
        envelope = i / rampSamples;
      } else if (i > bufferLength - rampSamples) {
        envelope = (bufferLength - i) / rampSamples;
      }

      data[i] = sample * envelope;
    }

    return buffer;
  }

  /**
   * Generate connection established sound - brief confirmation tone
   */
  generateConnectionEstablished(): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const duration = 0.3;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    // Two-tone confirmation (similar to modem ready)
    const freq1 = 1400;
    const freq2 = 1800;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const progress = i / bufferLength;

      let sample: number;
      if (progress < 0.5) {
        sample = Math.sin(2 * Math.PI * freq1 * t);
      } else {
        sample = Math.sin(2 * Math.PI * freq2 * t);
      }

      // Envelope
      let envelope = 1;
      const rampSamples = sampleRate * 0.01;
      if (i < rampSamples) {
        envelope = i / rampSamples;
      } else if (i > bufferLength - rampSamples) {
        envelope = (bufferLength - i) / rampSamples;
      }

      // Quick dip between tones
      const midPoint = bufferLength / 2;
      const dipWidth = sampleRate * 0.02;
      if (Math.abs(i - midPoint) < dipWidth) {
        envelope *= Math.abs(i - midPoint) / dipWidth;
      }

      data[i] = sample * 0.25 * envelope;
    }

    return buffer;
  }

  /**
   * Generate complete modem connect sequence (dial tone + dialing + handshake)
   * This creates the full 1980s modem connection experience
   */
  generateFullModemConnect(phoneNumber: string = '555-0142'): AudioBuffer {
    const sampleRate = this.context.sampleRate;

    // Timing for each phase
    const dialToneDuration = 0.5;
    const dialingDuration = phoneNumber.replace(/[^0-9*#]/g, '').length * 0.15;
    const pauseAfterDialing = 0.3;
    const ringDuration = 0.8;
    const handshakeDuration = 2.0;
    const connectionDuration = 0.3;

    const totalDuration =
      dialToneDuration +
      dialingDuration +
      pauseAfterDialing +
      ringDuration +
      handshakeDuration +
      connectionDuration;

    const bufferLength = Math.ceil(sampleRate * totalDuration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    let currentOffset = 0;

    // 1. Dial tone
    const dialTone = this.generateDialTone(dialToneDuration);
    const dialToneData = dialTone.getChannelData(0);
    for (let i = 0; i < dialToneData.length; i++) {
      data[currentOffset + i] = dialToneData[i];
    }
    currentOffset += dialToneData.length;

    // 2. DTMF dialing
    const dialing = this.generatePhoneDialing(phoneNumber);
    const dialingData = dialing.getChannelData(0);
    for (let i = 0; i < dialingData.length; i++) {
      data[currentOffset + i] = dialingData[i];
    }
    currentOffset += Math.ceil(sampleRate * dialingDuration);

    // 3. Pause
    currentOffset += Math.ceil(sampleRate * pauseAfterDialing);

    // 4. Ring tone (brief, as if answered quickly)
    const ringSamples = Math.ceil(sampleRate * ringDuration);
    for (let i = 0; i < ringSamples; i++) {
      const t = i / sampleRate;
      // US ring tone: 440Hz + 480Hz, with on/off pattern
      const ringOn = Math.floor(t * 2) % 2 === 0;
      if (ringOn) {
        data[currentOffset + i] =
          (Math.sin(2 * Math.PI * 440 * t) + Math.sin(2 * Math.PI * 480 * t)) * 0.15;
      }
    }
    currentOffset += ringSamples;

    // 5. Modem handshake
    const handshake = this.generateHandshakeNoise(handshakeDuration);
    const handshakeData = handshake.getChannelData(0);
    for (let i = 0; i < handshakeData.length; i++) {
      if (currentOffset + i < bufferLength) {
        data[currentOffset + i] = handshakeData[i];
      }
    }
    currentOffset += handshakeData.length;

    // 6. Connection established
    const connection = this.generateConnectionEstablished();
    const connectionData = connection.getChannelData(0);
    for (let i = 0; i < connectionData.length; i++) {
      if (currentOffset + i < bufferLength) {
        data[currentOffset + i] = connectionData[i];
      }
    }

    return buffer;
  }

  /**
   * Play an AudioBuffer through the modem sound output
   */
  playBuffer(buffer: AudioBuffer): AudioBufferSourceNode {
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);
    source.start(this.context.currentTime);
    return source;
  }

  /**
   * Play the full modem connect sequence
   */
  playModemConnect(phoneNumber?: string): AudioBufferSourceNode {
    const buffer = this.generateFullModemConnect(phoneNumber);
    return this.playBuffer(buffer);
  }

  /**
   * Play dial tone
   */
  playDialTone(duration?: number): AudioBufferSourceNode {
    const buffer = this.generateDialTone(duration);
    return this.playBuffer(buffer);
  }

  /**
   * Play phone dialing
   */
  playPhoneDialing(phoneNumber: string): AudioBufferSourceNode {
    const buffer = this.generatePhoneDialing(phoneNumber);
    return this.playBuffer(buffer);
  }

  /**
   * Play handshake noise
   */
  playHandshake(duration?: number): AudioBufferSourceNode {
    const buffer = this.generateHandshakeNoise(duration);
    return this.playBuffer(buffer);
  }

  /**
   * Play carrier tone
   */
  playCarrierTone(frequency?: number, duration?: number): AudioBufferSourceNode {
    const buffer = this.generateCarrierTone(frequency, duration);
    return this.playBuffer(buffer);
  }

  /**
   * Play connection established sound
   */
  playConnectionEstablished(): AudioBufferSourceNode {
    const buffer = this.generateConnectionEstablished();
    return this.playBuffer(buffer);
  }

  /**
   * Generate the 2600Hz "blue box" tone
   * This frequency was used to seize long-distance trunk lines in the 1960s-1980s
   */
  generate2600HzTone(duration: number = 1.0): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const freq = 2600;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const sample = Math.sin(2 * Math.PI * freq * t);

      // Envelope with quick attack/release
      let envelope = 1;
      const attackSamples = sampleRate * 0.01;
      const releaseSamples = sampleRate * 0.02;

      if (i < attackSamples) {
        envelope = i / attackSamples;
      } else if (i > bufferLength - releaseSamples) {
        envelope = (bufferLength - i) / releaseSamples;
      }

      data[i] = sample * 0.25 * envelope;
    }

    return buffer;
  }

  /**
   * Generate a single extended DTMF tone (including A, B, C, D)
   */
  generateExtendedDTMFTone(char: string, duration: number = 0.08): AudioBuffer | null {
    const frequencies = DTMF_EXTENDED[char.toUpperCase()];
    if (!frequencies) return null;

    if (frequencies[0] === 0 && frequencies[1] === 0) {
      const sampleRate = this.context.sampleRate;
      const bufferLength = Math.ceil(sampleRate * 0.04);
      return this.context.createBuffer(1, bufferLength, sampleRate);
    }

    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const [lowFreq, highFreq] = frequencies;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const low = Math.sin(2 * Math.PI * lowFreq * t);
      const high = Math.sin(2 * Math.PI * highFreq * t);

      let envelope = 1;
      const attackSamples = sampleRate * 0.003;
      const releaseSamples = sampleRate * 0.008;

      if (i < attackSamples) {
        envelope = i / attackSamples;
      } else if (i > bufferLength - releaseSamples) {
        envelope = (bufferLength - i) / releaseSamples;
      }

      data[i] = (low + high) * 0.12 * envelope;
    }

    return buffer;
  }

  /**
   * Generate 24 random international DTMF routing tones
   * Simulates MF (Multi-Frequency) trunk signaling used in 1980s international calls
   * Faster pacing for efficient trunk routing
   */
  generateRandomInternationalTones(count: number = 24): AudioBuffer {
    const toneDuration = 0.06;  // Faster tone duration (50% speedup)
    const gapDuration = 0.03;   // Shorter gaps between tones
    const totalDuration = count * (toneDuration + gapDuration);

    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * totalDuration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    // Extended character set for international trunk signaling
    const trunkChars = '0123456789ABCD*#';

    let currentSample = 0;
    const samplesPerTone = Math.ceil(sampleRate * toneDuration);
    const samplesPerGap = Math.ceil(sampleRate * gapDuration);

    for (let i = 0; i < count; i++) {
      const char = trunkChars[Math.floor(Math.random() * trunkChars.length)];
      const toneBuffer = this.generateExtendedDTMFTone(char, toneDuration);

      if (toneBuffer) {
        const toneData = toneBuffer.getChannelData(0);
        for (let j = 0; j < toneData.length && currentSample + j < bufferLength; j++) {
          data[currentSample + j] = toneData[j];
        }
      }
      currentSample += samplesPerTone + samplesPerGap;
    }

    return buffer;
  }

  /**
   * Generate realistic phone ringing sound (European style)
   * European standard: 425Hz single frequency with authentic cadence
   * Pattern: 1.0 second ON, 4.0 seconds OFF (standard continental Europe cadence)
   * This is used in Germany, France, UK (variants), and most of continental Europe
   */
  generateRinging(rings: number = 2): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const ringOnDuration = 1.0;    // European ring: 1 second ON
    const ringOffDuration = 4.0;   // European ring: 4 seconds OFF (natural pacing)
    const totalDuration = rings * ringOnDuration + (rings - 1) * ringOffDuration;

    const bufferLength = Math.ceil(sampleRate * totalDuration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    let currentOffset = 0;

    for (let ring = 0; ring < rings; ring++) {
      const ringSamples = Math.ceil(sampleRate * ringOnDuration);

      for (let i = 0; i < ringSamples; i++) {
        const t = i / sampleRate;
        const idx = currentOffset + i;
        if (idx < bufferLength) {
          // European ring tone: 425Hz single frequency (authentic standard frequency)
          // This produces the characteristic European phone buzz
          const tone425 = Math.sin(2 * Math.PI * 425 * t);

          // European phones have a buzzy, harsh character - slightly higher amplitude
          let sample = tone425 * 0.22;

          // Realistic envelope - gradual attack/release like a real phone bell
          let envelope = 1;
          const attackTime = 0.05;   // 50ms attack (slightly faster than US)
          const releaseTime = 0.08;  // 80ms release
          const attackSamples = sampleRate * attackTime;
          const releaseSamples = sampleRate * releaseTime;

          if (i < attackSamples) {
            // Smooth attack curve
            envelope = Math.sin((i / attackSamples) * Math.PI * 0.5);
          } else if (i > ringSamples - releaseSamples) {
            // Smooth release curve
            const releaseProgress = (ringSamples - i) / releaseSamples;
            envelope = Math.sin(releaseProgress * Math.PI * 0.5);
          }

          data[idx] = sample * envelope;
        }
      }

      currentOffset += Math.ceil(sampleRate * (ringOnDuration + ringOffDuration));
    }

    return buffer;
  }

  /**
   * Generate modem pickup and connection sound
   * The classic screech when a modem answers
   */
  generateModemPickup(duration: number = 1.8): AudioBuffer {
    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    let phase1 = 0, phase2 = 0, phase3 = 0;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const progress = i / bufferLength;
      let sample = 0;

      if (progress < 0.15) {
        // Initial answer tone - 2100Hz
        const localProgress = progress / 0.15;
        phase1 += (2 * Math.PI * 2100) / sampleRate;
        sample = Math.sin(phase1) * 0.3 * localProgress;
      } else if (progress < 0.4) {
        // Carrier negotiation - frequency sweep
        const localProgress = (progress - 0.15) / 0.25;
        const freq = 1200 + Math.sin(localProgress * Math.PI * 4) * 800;
        phase2 += (2 * Math.PI * freq) / sampleRate;
        sample = Math.sin(phase2) * 0.25;

        // Add some digital-sounding bursts
        if (Math.random() < 0.15) {
          sample += (Math.random() - 0.5) * 0.2;
        }
      } else if (progress < 0.75) {
        // V.32 negotiation sounds - rapid alternating tones
        const localProgress = (progress - 0.4) / 0.35;
        const altRate = 15 + localProgress * 25;
        const altFreq = Math.floor(t * altRate) % 2 === 0 ? 1800 : 2400;
        phase3 += (2 * Math.PI * altFreq) / sampleRate;
        sample = Math.sin(phase3) * 0.2;

        // FSK data simulation
        const dataFreq = 1200 + Math.sin(t * 150) * 400;
        sample += Math.sin(2 * Math.PI * dataFreq * t) * 0.1;
      } else {
        // Connection established - settling carrier
        const fadeProgress = (progress - 0.75) / 0.25;
        const freq = 1200;
        phase1 += (2 * Math.PI * freq) / sampleRate;
        sample = Math.sin(phase1) * 0.2 * (1 - fadeProgress * 0.8);
      }

      // Overall envelope
      let envelope = 1;
      const rampSamples = sampleRate * 0.03;
      if (i < rampSamples) {
        envelope = i / rampSamples;
      } else if (i > bufferLength - rampSamples) {
        envelope = (bufferLength - i) / rampSamples;
      }

      data[i] = sample * envelope;
    }

    return buffer;
  }

  /**
   * Generate a standard 7-digit phone number (NXX-XXXX format)
   * Used for the initial dial before trunk seizure
   */
  static generate7DigitNumber(): { number: string; formatted: string } {
    // First digit of exchange can't be 0 or 1
    const exchange1 = Math.floor(Math.random() * 8) + 2; // 2-9
    const exchange2 = Math.floor(Math.random() * 10);
    const exchange3 = Math.floor(Math.random() * 10);

    // Subscriber number - 4 digits
    let subscriber = '';
    for (let i = 0; i < 4; i++) {
      subscriber += Math.floor(Math.random() * 10);
    }

    const number = `${exchange1}${exchange2}${exchange3}${subscriber}`;
    const formatted = `${exchange1}${exchange2}${exchange3}-${subscriber}`;

    return { number, formatted };
  }

  /**
   * Generate 24 random routing digits for international trunk signaling
   */
  static generate24RoutingDigits(): string {
    const chars = '0123456789ABCD*#';
    let digits = '';
    for (let i = 0; i < 24; i++) {
      digits += chars[Math.floor(Math.random() * chars.length)];
    }
    return digits;
  }

  /**
   * Generate the full authentic war dialing sequence:
   * 1. Dial 7-digit phone number with DTMF
   * 2. Pause
   * 3. 2600Hz carrier/trunk seizure tone
   * 4. 24 random international DTMF routing tones (faster pacing)
   * 5. Two rings (realistic phone ringing)
   * 6. Modem pickup and connection
   */
  generateWarDialSequence(phoneNumber?: string): {
    buffer: AudioBuffer;
    phoneData: { number: string; formatted: string };
    routingDigits: string;
    timeline: Array<{ phase: string; startTime: number; duration: number; description: string }>;
  } {
    const sampleRate = this.context.sampleRate;

    // Generate 7-digit phone number if not provided
    const phoneData = phoneNumber
      ? { number: phoneNumber.replace(/\D/g, ''), formatted: phoneNumber }
      : ModemSounds.generate7DigitNumber();

    // Generate the 24 routing digits
    const routingDigits = ModemSounds.generate24RoutingDigits();

    // Calculate durations for each phase
    const dialDuration = 7 * 0.15;  // 7 digits with standard DTMF timing
    const pauseDuration = 0.8;      // Extended pause for text description display
    const toneDuration2600 = 1.0;   // Slightly longer for dramatic effect
    const routingTonesDuration = 24 * 0.09;  // Faster pacing (0.06s tone + 0.03s gap) - 50% speedup
    const ringDuration = 5.5;       // 2 rings with realistic timing (2s on, 1.5s gap)
    const modemPickupDuration = 1.8;

    const totalDuration = dialDuration + pauseDuration + toneDuration2600 +
                          routingTonesDuration + pauseDuration + ringDuration + modemPickupDuration;

    const bufferLength = Math.ceil(sampleRate * totalDuration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    let currentOffset = 0;
    const timeline: Array<{ phase: string; startTime: number; duration: number; description: string }> = [];

    // Phase 1: Dial the 7-digit number
    timeline.push({
      phase: 'dialing',
      startTime: currentOffset / sampleRate,
      duration: dialDuration,
      description: `DIALING: ${phoneData.formatted}`
    });

    const dialBuffer = this.generatePhoneDialing(phoneData.number);
    const dialData = dialBuffer.getChannelData(0);
    for (let i = 0; i < dialData.length && currentOffset + i < bufferLength; i++) {
      data[currentOffset + i] = dialData[i];
    }
    currentOffset += Math.ceil(sampleRate * dialDuration);

    // Phase 2: Pause - waiting for connection
    timeline.push({
      phase: 'pause',
      startTime: currentOffset / sampleRate,
      duration: pauseDuration,
      description: 'WAITING FOR CARRIER...'
    });
    currentOffset += Math.ceil(sampleRate * pauseDuration);

    // Phase 3: 2600Hz trunk seizure tone
    timeline.push({
      phase: '2600hz',
      startTime: currentOffset / sampleRate,
      duration: toneDuration2600,
      description: '2600Hz TONE - TRUNK SEIZED'
    });

    const tone2600 = this.generate2600HzTone(toneDuration2600);
    const tone2600Data = tone2600.getChannelData(0);
    for (let i = 0; i < tone2600Data.length && currentOffset + i < bufferLength; i++) {
      data[currentOffset + i] = tone2600Data[i];
    }
    currentOffset += Math.ceil(sampleRate * toneDuration2600);

    // Phase 4: 24 random international routing tones (MF signaling)
    timeline.push({
      phase: 'routing',
      startTime: currentOffset / sampleRate,
      duration: routingTonesDuration,
      description: `MF ROUTING: ${routingDigits}`
    });

    const routingBuffer = this.generateRandomInternationalTones(24);
    const routingData = routingBuffer.getChannelData(0);
    for (let i = 0; i < routingData.length && currentOffset + i < bufferLength; i++) {
      data[currentOffset + i] = routingData[i];
    }
    currentOffset += Math.ceil(sampleRate * routingTonesDuration);

    // Phase 5: Brief pause before ringing
    timeline.push({
      phase: 'connecting',
      startTime: currentOffset / sampleRate,
      duration: pauseDuration,
      description: 'ROUTING COMPLETE - CONNECTING...'
    });
    currentOffset += Math.ceil(sampleRate * pauseDuration);

    // Phase 6: Realistic phone ringing (2 rings)
    timeline.push({
      phase: 'ringing',
      startTime: currentOffset / sampleRate,
      duration: ringDuration,
      description: 'RINGING...'
    });

    const ringBuffer = this.generateRinging(2);
    const ringData = ringBuffer.getChannelData(0);
    for (let i = 0; i < ringData.length && currentOffset + i < bufferLength; i++) {
      data[currentOffset + i] = ringData[i];
    }
    currentOffset += Math.ceil(sampleRate * ringDuration);

    // Phase 7: Modem picks up and connects
    timeline.push({
      phase: 'modem',
      startTime: currentOffset / sampleRate,
      duration: modemPickupDuration,
      description: 'MODEM ANSWERING - ESTABLISHING CONNECTION'
    });

    const modemBuffer = this.generateModemPickup(modemPickupDuration);
    const modemData = modemBuffer.getChannelData(0);
    for (let i = 0; i < modemData.length && currentOffset + i < bufferLength; i++) {
      data[currentOffset + i] = modemData[i];
    }

    return { buffer, phoneData, routingDigits, timeline };
  }

  /**
   * Play the full war dial sequence
   */
  playWarDialSequence(phoneNumber?: string): {
    source: AudioBufferSourceNode;
    phoneData: { number: string; formatted: string };
    routingDigits: string;
    timeline: Array<{ phase: string; startTime: number; duration: number; description: string }>;
  } {
    const { buffer, phoneData, routingDigits, timeline } = this.generateWarDialSequence(phoneNumber);
    const source = this.playBuffer(buffer);
    return { source, phoneData, routingDigits, timeline };
  }
}
